import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Lock, Trash2, ShieldCheck, KeyRound, ChevronRight, X } from "lucide-react";
import { useWallet } from "@/context/WalletContext";
import { CHAINS, addressFor, shorten } from "@/lib/chains";
import { decryptVault } from "@/lib/wallet";
import PinPad from "@/components/PinPad";
import ChainIcon from "@/components/ChainIcon";

export default function Settings() {
  const nav = useNavigate();
  const { accounts, lock, reset } = useWallet();
  const [reveal, setReveal] = useState(false);
  const [seed, setSeed] = useState("");
  const [pinErr, setPinErr] = useState("");

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };

  const tryReveal = (pin) => {
    const m = decryptVault(pin);
    if (!m) { setPinErr("Incorrect PIN"); return; }
    setSeed(m);
    setPinErr("");
  };

  return (
    <div className="px-5 pt-6">
      <h1 className="font-outfit text-2xl font-extrabold">Settings</h1>

      {/* addresses */}
      <p className="mb-3 mt-6 font-outfit text-sm font-semibold text-[#9CA3AF]">Your addresses</p>
      <div className="space-y-2">
        {CHAINS.map((c) => (
          <div key={c.id} className="flex items-center gap-3 card-surface p-3.5">
            <ChainIcon chain={c} size={36} />
            <div className="min-w-0 flex-1">
              <p className="font-outfit text-xs font-semibold text-white">{c.name}</p>
              <p className="font-rajdhani text-xs text-[#9CA3AF]">{shorten(addressFor(accounts, c.id), 10)}</p>
            </div>
            <button data-testid={`copy-address-${c.id}`} onClick={() => copy(addressFor(accounts, c.id))} className="text-[#6B7280] hover:text-neon">
              <Copy className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      {/* security */}
      <p className="mb-3 mt-7 font-outfit text-sm font-semibold text-[#9CA3AF]">Security</p>
      <div className="space-y-2">
        <button data-testid="reveal-seed-button" onClick={() => { setReveal(true); setSeed(""); setPinErr(""); }} className="flex w-full items-center gap-3 card-surface p-4 hover:border-neon/30">
          <KeyRound className="h-5 w-5 text-neon" />
          <span className="flex-1 text-left font-outfit text-sm font-semibold text-white">Reveal recovery phrase</span>
          <ChevronRight className="h-4 w-4 text-[#6B7280]" />
        </button>
        <button data-testid="lock-wallet-button" onClick={() => { lock(); nav("/unlock"); }} className="flex w-full items-center gap-3 card-surface p-4 hover:border-neon/30">
          <Lock className="h-5 w-5 text-neon" />
          <span className="flex-1 text-left font-outfit text-sm font-semibold text-white">Lock wallet</span>
          <ChevronRight className="h-4 w-4 text-[#6B7280]" />
        </button>
        <button
          data-testid="reset-wallet-button"
          onClick={() => { if (window.confirm("Remove this wallet from this device? Make sure you have your recovery phrase.")) { reset(); nav("/onboarding"); } }}
          className="flex w-full items-center gap-3 rounded-2xl border border-[#EF4444]/30 bg-[#EF4444]/5 p-4 hover:bg-[#EF4444]/10"
        >
          <Trash2 className="h-5 w-5 text-[#EF4444]" />
          <span className="flex-1 text-left font-outfit text-sm font-semibold text-[#EF4444]">Reset wallet</span>
        </button>
      </div>

      <div className="mt-7 flex items-start gap-3 rounded-2xl border border-[#10B981]/25 bg-[#10B981]/5 p-4">
        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#10B981]" />
        <p className="font-sans text-xs leading-relaxed text-[#9CA3AF]">
          <span className="font-semibold text-[#10B981]">Zero-knowledge.</span> Your seed and private keys are
          AES-256 encrypted and stored only in this browser. CoreWallet servers act purely as a public RPC relay.
        </p>
      </div>

      <button onClick={() => nav("/admin")} className="mt-6 w-full py-2 text-center font-sans text-xs text-[#6B7280] hover:text-neon">
        Admin gateway →
      </button>
      <div className="h-4" />

      {/* reveal modal */}
      {reveal && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 px-6" onClick={() => setReveal(false)}>
          <div className="w-full max-w-sm card-surface p-6" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-outfit text-lg font-bold">{seed ? "Recovery phrase" : "Enter PIN"}</h2>
              <button onClick={() => setReveal(false)} className="text-[#6B7280] hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            {!seed ? (
              <PinPad error={pinErr} onComplete={tryReveal} />
            ) : (
              <div>
                <div className="grid grid-cols-3 gap-2">
                  {seed.split(" ").map((w, i) => (
                    <div key={i} className="flex items-center gap-1.5 rounded-lg border border-[#1F2430] bg-[#12141A] px-2 py-2">
                      <span className="font-rajdhani text-[10px] text-[#6B7280]">{i + 1}</span>
                      <span className="font-rajdhani text-xs font-semibold text-white">{w}</span>
                    </div>
                  ))}
                </div>
                <button onClick={() => copy(seed)} className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-[#1F2430] py-3 font-sans text-sm text-[#9CA3AF] hover:text-white">
                  <Copy className="h-4 w-4" /> Copy phrase
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
